import React from "react";
import { Box, Tabs, Tab, Typography } from "@mui/material";

export interface ParentTabsProps {
  parentName1: string;
  parentName2: string;
  activeParent: "parent1" | "parent2";
  onTabChange: (parent: "parent1" | "parent2") => void;
}

/**
 * WCAG-compliant colors with sufficient contrast:
 * - parent1: #0277BD (4.74:1 on white)
 * - parent2: #7B1FA2 (7.06:1 on white)
 */
const COLORS = {
  parent1: "#0277BD",
  parent2: "#7B1FA2",
};

const ParentTabs: React.FC<ParentTabsProps> = ({
  parentName1,
  parentName2,
  activeParent,
  onTabChange,
}) => {
  const tabIndex = activeParent === "parent1" ? 0 : 1;

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    onTabChange(newValue === 0 ? "parent1" : "parent2");
  };

  const activeParentName = activeParent === "parent1" ? parentName1 : parentName2;

  return (
    <Box>
      {/* 
        DO NOT add role="tablist" here — MUI Tabs already applies it to the 
        inner flexContainer. Adding it again causes axe to flag missing child roles.
      */}
      <Tabs
        value={tabIndex}
        onChange={handleChange}
        aria-label="Select active parent for calendar assignment"
        TabIndicatorProps={{
          style: {
            backgroundColor: COLORS[activeParent],
            height: 3,
          },
        }}
      >
        <Tab
          id="parent-tab-0"
          aria-controls="parent-tabpanel"
          label={parentName1}
          aria-label={`Assign days to ${parentName1}`}
          sx={{
            minHeight: 44,
            minWidth: 44,
            color: activeParent === "parent1" ? COLORS.parent1 : "text.secondary",
            fontWeight: activeParent === "parent1" ? 700 : 400,
            "&.Mui-selected": {
              color: COLORS.parent1,
            },
          }}
        />
        <Tab
          id="parent-tab-1"
          aria-controls="parent-tabpanel"
          label={parentName2}
          aria-label={`Assign days to ${parentName2}`}
          sx={{
            minHeight: 44,
            minWidth: 44,
            color: activeParent === "parent2" ? COLORS.parent2 : "text.secondary",
            fontWeight: activeParent === "parent2" ? 700 : 400,
            "&.Mui-selected": {
              color: COLORS.parent2,
            },
          }}
        />
      </Tabs>

      {/* 
        Tab panel — associated with the active tab via aria-labelledby.
        Content is rendered below in the parent component, this provides 
        the ARIA relationship.
      */}
      <Box
        id="parent-tabpanel"
        role="tabpanel"
        aria-labelledby={`parent-tab-${tabIndex}`}
        tabIndex={0}
      />

      {/* Live region to announce active parent change to screen readers */}
      <Typography
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          whiteSpace: "nowrap",
        }}
      >
        {`Now assigning days to ${activeParentName}. Click or press Enter on calendar dates to assign.`}
      </Typography>
    </Box>
  );
};

export default ParentTabs;
